/**
 * SafetyBaselineCard — admin control for the "Days Since Last Injury"
 * KPI on the Dashboard.
 *
 * Teams migrating to FieldOps mid-year already have OSHA history
 * pre-dating the system. Forcing them to backfill a fake "historical
 * injury" incident record just to seed the counter is ugly (fake row
 * shows up on the incidents list, in exports, etc). Instead, they
 * type one date here and the dashboard fallback picks it up. As soon
 * as a real injury incident is filed in the system, the incident
 * wins and this value becomes irrelevant.
 *
 * Backed by GET/POST /api/reports/safety-baseline. Admin-only.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

function daysBetween(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  } catch { return null; }
}

export default function SafetyBaselineCard() {
  const [loaded, setLoaded] = useState(false);
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/reports/safety-baseline").then((r) => {
      setCurrent(r.data.last_recordable_injury_baseline_date || null);
      setDraft(r.data.last_recordable_injury_baseline_date || "");
      setLoaded(true);
    }).catch(() => setLoaded(true));
  };
  useEffect(() => { load(); }, []);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const previewDays = useMemo(() => daysBetween(draft), [draft]);
  const currentDays = useMemo(() => daysBetween(current), [current]);
  const dirty = (draft || "") !== (current || "");

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/reports/safety-baseline", {
        last_recordable_injury_baseline_date: draft || null,
      });
      const val = r.data.last_recordable_injury_baseline_date || null;
      setCurrent(val);
      setDraft(val || "");
      toast.success(val ? "Baseline saved" : "Baseline cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setDraft("");
    setSaving(true);
    try {
      await api.post("/reports/safety-baseline", {
        last_recordable_injury_baseline_date: null,
      });
      setCurrent(null);
      toast.success("Baseline cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="safety-baseline-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-display font-bold uppercase tracking-wider text-slate-200 flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-emerald-400" />
          Days Since Last Recordable Injury — Baseline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-400 leading-relaxed">
          Seeds the Dashboard KPI with your pre-FieldOps OSHA history.
          Filters to <span className="text-slate-200 font-semibold">OSHA-recordable</span> injuries only —
          first-aid-only cases do not reset the counter. As soon as a
          recordable injury incident is filed in the system, that
          incident supersedes this baseline automatically. Leave blank
          if you'd rather show nothing until a real incident is filed.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] text-slate-500 font-mono uppercase tracking-wider block mb-1.5">
              Last OSHA-recordable injury date
            </label>
            <Input
              type="date"
              value={draft}
              max={today}
              onChange={(e) => setDraft(e.target.value)}
              className="bg-slate-950 border-slate-800 text-slate-100"
              data-testid="safety-baseline-date-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="safety-baseline-save-btn"
              className="bg-emerald-700 hover:bg-emerald-600 text-white"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            {current && (
              <Button
                variant="outline"
                onClick={clear}
                disabled={saving}
                data-testid="safety-baseline-clear-btn"
                className="border-slate-700 hover:bg-slate-800"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {(current || previewDays !== null) && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-800">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Current baseline</div>
              <div className="mt-1 text-slate-100 font-mono text-sm" data-testid="safety-baseline-current">
                {current ? `${current}  ·  ${currentDays?.toLocaleString()} days` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Preview after save</div>
              <div className="mt-1 text-emerald-300 font-mono text-sm" data-testid="safety-baseline-preview">
                {draft ? `${draft}  ·  ${previewDays?.toLocaleString()} days` : "Cleared"}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
